import React, { useRef, useState, useEffect } from "react";
import { View, Pressable, StyleSheet, ViewStyle } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useContextMenu from "@/hooks/useContextMenu";
import Animated, { useAnimatedStyle, withTiming, useSharedValue } from "react-native-reanimated";

type Option = {
  label: string;
  value: string;
};

type InlineDropdownProps = {
  options: Option[];
  value?: string;
  placeholder?: string;
  onSelect: (value: string) => void;
  containerStyle?: ViewStyle;
  maxHeight?: number;
};

export function InlineDropdown({
  options,
  value,
  placeholder = "Select...",
  onSelect,
  containerStyle,
  maxHeight = 200,
}: InlineDropdownProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const selectorRef = useRef<View>(null);
  const rotation = useSharedValue(0);
  const [isOpen, setIsOpen] = useState(false);

  // Reset chevron when menu closes externally
  useEffect(() => {
    if (!contextMenu.visible && isOpen) {
      rotation.value = withTiming(0, { duration: 200 });
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu.visible]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleToggle = () => {
    selectorRef.current?.measureInWindow((x, y, w, h) => {
      rotation.value = withTiming(180, { duration: 200 });
      setIsOpen(true);
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 4 });
      contextMenu.setMenuContent(
        <Animated.ScrollView style={{ maxHeight, minWidth: w }}>
          {options.map((option, index) => (
            <Pressable
              key={option.value}
              style={({ pressed }) => [
                styles.option,
                {
                  backgroundColor: pressed
                    ? colors.backgroundSecondary
                    : option.value === value
                      ? colors.backgroundSecondary
                      : "transparent",
                  borderBottomWidth: index < options.length - 1 ? StyleSheet.hairlineWidth : 0,
                  borderBottomColor: colors.border,
                },
              ]}
              onPress={() => {
                onSelect(option.value);
                contextMenu.setVisible(false);
                rotation.value = withTiming(0, { duration: 200 });
                setIsOpen(false);
              }}
            >
              <ThemedText
                style={[
                  styles.optionText,
                  {
                    color: option.value === value ? colors.primary : colors.text,
                    fontWeight: option.value === value ? "600" : "400",
                  },
                ]}
              >
                {option.label}
              </ThemedText>
              {option.value === value && <MaterialIcons name="check" size={20} color={colors.primary} />}
            </Pressable>
          ))}
        </Animated.ScrollView>,
      );
      contextMenu.setVisible(true);
    });
  };

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <View style={[styles.container, containerStyle]} ref={selectorRef}>
      <Pressable
        style={[
          styles.selector,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
          },
        ]}
        onPress={handleToggle}
      >
        <ThemedText style={[styles.selectorText, { color: selectedOption ? colors.text : colors.placeholder }]}>
          {selectedOption?.label || placeholder}
        </ThemedText>
        <Animated.View style={animatedIconStyle}>
          <MaterialIcons name="keyboard-arrow-down" size={24} color={colors.text} />
        </Animated.View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  selector: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 45,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  selectorText: {
    fontSize: 16,
    flex: 1,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  optionText: {
    fontSize: 16,
    flex: 1,
  },
});
